import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Loader2, Home, Calendar } from 'lucide-react';
import { api, AED } from '../lib/api';
import { toast } from 'sonner';

/**
 * Modal that captures tenant name, lease dates, and annual rent, then
 * atomically flips the property to `stage: rented`.
 * Opens either from the "Mark as Rented" pipeline action or when the user
 * selects "Rented" from the stage dropdown on PropertyDetail.
 */
export default function RentPropertyModal({ open, onClose, property, onDone }) {
  const [form, setForm] = useState(() => ({
    tenant_name: property?.tenant_name || '',
    lease_start_date: property?.lease_start_date || new Date().toISOString().slice(0, 10),
    lease_end_date: property?.lease_end_date || new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().slice(0, 10),
    annual_rent_aed: property?.annual_rent_aed || '',
    service_charge_aed_pa: property?.service_charge_aed_pa || '',
    management_fee_pct: property?.management_fee_pct ?? '',
  }));
  const [busy, setBusy] = useState(false);

  if (!open || !property) return null;

  const submit = async (e) => {
    e.preventDefault();
    const rent = parseFloat(form.annual_rent_aed);
    if (!form.tenant_name.trim() || !form.lease_start_date || !form.lease_end_date || !rent || rent <= 0) {
      toast.error('Tenant name, both lease dates, and a positive annual rent are required.');
      return;
    }
    if (form.lease_end_date <= form.lease_start_date) {
      toast.error('Lease end date must be after start date.');
      return;
    }
    setBusy(true);
    try {
      const payload = {
        tenant_name: form.tenant_name.trim(),
        lease_start_date: form.lease_start_date,
        lease_end_date: form.lease_end_date,
        annual_rent_aed: rent,
      };
      if (form.service_charge_aed_pa !== '') payload.service_charge_aed_pa = parseFloat(form.service_charge_aed_pa) || 0;
      if (form.management_fee_pct !== '') payload.management_fee_pct = parseFloat(form.management_fee_pct);
      await api.post(`/properties/${property.id}/rent`, payload);
      toast.success(`${property.title} is now rented to ${payload.tenant_name}`);
      onDone?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to mark as rented');
    } finally { setBusy(false); }
  };

  const monthly = parseFloat(form.annual_rent_aed) ? parseFloat(form.annual_rent_aed) / 12 : 0;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-slate-900/40 backdrop-blur-sm"
         onClick={onClose} data-testid="rent-property-modal">
      <motion.form initial={{opacity:0,y:20}} animate={{opacity:1,y:0}}
                    onSubmit={submit} onClick={(e)=>e.stopPropagation()}
                    className="glass-strong w-full max-w-lg p-8 space-y-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="overline flex items-center gap-1.5"><Home size={11}/> Exit path · Rent</div>
            <div className="font-serif text-3xl mt-1">Mark as rented</div>
            <div className="text-xs text-slate-500 mt-1">{property.title}</div>
          </div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-white/60 rounded-full"><X size={18}/></button>
        </div>

        <label className="block">
          <div className="overline mb-1">Tenant name</div>
          <input value={form.tenant_name}
                  onChange={(e)=>setForm({...form, tenant_name: e.target.value})}
                  data-testid="rent-tenant-input"
                  placeholder="e.g. Ahmed Al-Mansoori"
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-white/80 text-sm"/>
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <div className="overline mb-1 flex items-center gap-1"><Calendar size={10}/> Lease start</div>
            <input type="date" value={form.lease_start_date}
                    onChange={(e)=>setForm({...form, lease_start_date: e.target.value})}
                    data-testid="rent-start-input"
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-white/80 text-sm"/>
          </label>
          <label className="block">
            <div className="overline mb-1 flex items-center gap-1"><Calendar size={10}/> Lease end</div>
            <input type="date" value={form.lease_end_date}
                    onChange={(e)=>setForm({...form, lease_end_date: e.target.value})}
                    data-testid="rent-end-input"
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-white/80 text-sm"/>
          </label>
        </div>

        <label className="block">
          <div className="overline mb-1">Annual rent (AED)</div>
          <input type="number" min="0" step="1000"
                  value={form.annual_rent_aed}
                  onChange={(e)=>setForm({...form, annual_rent_aed: e.target.value})}
                  data-testid="rent-annual-input"
                  placeholder="e.g. 180000"
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-white/80 text-sm font-mono"/>
          {monthly > 0 && (
            <div className="text-[11px] text-slate-500 mt-1">
              ≈ {AED(monthly)} / month
            </div>
          )}
        </label>

        <div className="pt-2 border-t border-slate-200/50">
          <div className="overline mb-2">Yield inputs (optional · defaults from workspace settings)</div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="text-xs text-slate-500 mb-1">Service charge / year</div>
              <input type="number" min="0" step="100"
                      value={form.service_charge_aed_pa}
                      onChange={(e)=>setForm({...form, service_charge_aed_pa: e.target.value})}
                      data-testid="rent-svc-input"
                      placeholder="AED / year"
                      className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-white/80 text-sm font-mono"/>
            </label>
            <label className="block">
              <div className="text-xs text-slate-500 mb-1">Mgmt fee % of rent</div>
              <input type="number" min="0" max="30" step="0.5"
                      value={form.management_fee_pct}
                      onChange={(e)=>setForm({...form, management_fee_pct: e.target.value})}
                      data-testid="rent-mgmt-input"
                      placeholder="8"
                      className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-white/80 text-sm font-mono"/>
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn-ghost text-sm">Cancel</button>
          <button type="submit" disabled={busy}
                   data-testid="rent-submit-btn"
                   className="btn-primary text-sm bg-teal-600 hover:bg-teal-700 inline-flex items-center gap-2">
            {busy ? <><Loader2 size={14} className="animate-spin"/> Saving…</> : <>Move to Rented</>}
          </button>
        </div>
      </motion.form>
    </div>
  );
}
